import React from "react";
import { Link } from "react-router-dom";
import { Linkedin, Twitter, Facebook, Instagram, Mail, MapPin, Phone } from "lucide-react";
import { useApp } from "@/context/AppContext";

export default function Footer() {
  const { t } = useApp();
  const year = new Date().getFullYear();
  const links = [
    { to: "/", label: t("global", "nav_home") },
    { to: "/courtage", label: t("global", "nav_courtage") },
    { to: "/conseil", label: t("global", "nav_conseil") },
    { to: "/a-propos", label: t("global", "nav_apropos") },
    { to: "/contact", label: t("global", "nav_contact") },
  ];

  // Social icons only appear when a URL is set in the CMS (Global settings).
  const socials = [
    { key: "linkedin", Icon: Linkedin, url: t("global", "social_linkedin") },
    { key: "twitter", Icon: Twitter, url: t("global", "social_twitter") },
    { key: "facebook", Icon: Facebook, url: t("global", "social_facebook") },
    { key: "instagram", Icon: Instagram, url: t("global", "social_instagram") },
  ].filter((s) => s.url && s.url.trim());

  const email = t("global", "email");
  const phone = t("global", "phone");
  const phoneIsPlaceholder = !phone || /x/i.test(phone);

  return (
    <footer className="relative overflow-hidden bg-[#0A0A0A] text-white" data-testid="site-footer">
      <div className="mx-auto max-w-[1400px] px-5 pb-10 pt-20 md:px-10">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-6">
            <div className="font-montserrat text-3xl font-black tracking-tight">
              <span className="text-[#F5821F]">Agrivoire</span> <span className="text-[#1CADE4]">Export</span>{" "}
              <span className="text-xl text-[#8CC63F]" data-testid="footer-sarl">SARL</span>
            </div>
            <p className="mt-5 max-w-md font-poppins text-base leading-relaxed text-white/60">
              {t("global", "footer_tagline")}
            </p>
            {socials.length > 0 && (
              <div className="mt-6 flex gap-3" data-testid="footer-socials">
                {socials.map(({ key, Icon, url }) => (
                  <a
                    key={key}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`footer-social-${key}`}
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white/70 transition-colors hover:border-[#8CC63F] hover:text-[#8CC63F]"
                    aria-label={key}
                  >
                    <Icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
            )}
          </div>

          <div className="md:col-span-3">
            <h4 className="font-poppins text-xs font-bold uppercase tracking-[0.2em] text-[#8CC63F]">
              {t("global", "footer_quicklinks")}
            </h4>
            <ul className="mt-5 space-y-3">
              {links.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="font-poppins text-sm text-white/70 transition-colors hover:text-white" data-testid={`footer-link-${l.to}`}>
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <h4 className="font-poppins text-xs font-bold uppercase tracking-[0.2em] text-[#8CC63F]">Contact</h4>
            <ul className="mt-5 space-y-3 font-poppins text-sm text-white/70">
              <li>
                <Link to="/contact" className="flex items-center gap-2 transition-colors hover:text-white" data-testid="footer-contact-location">
                  <MapPin className="h-4 w-4 text-[#1CADE4]" /> {t("global", "country")}
                </Link>
              </li>
              <li>
                <a href={`mailto:${email}`} className="flex items-center gap-2 transition-colors hover:text-white" data-testid="footer-contact-email">
                  <Mail className="h-4 w-4 text-[#1CADE4]" /> {email}
                </a>
              </li>
              {!phoneIsPlaceholder && (
                <li>
                  <a href={`tel:${phone.replace(/\s+/g, "")}`} className="flex items-center gap-2 transition-colors hover:text-white" data-testid="footer-contact-phone">
                    <Phone className="h-4 w-4 text-[#1CADE4]" /> {phone}
                  </a>
                </li>
              )}
            </ul>
          </div>
        </div>

        <div className="mt-16 flex flex-col items-start justify-between gap-4 border-t border-white/10 pt-6 sm:flex-row sm:items-center">
          <p className="font-poppins text-xs text-white/40">
            © {year} Agrivoire Export SARL. {t("global", "footer_rights")}
          </p>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <Link to="/mentions-legales" data-testid="footer-legal-link" className="font-poppins text-xs text-white/40 transition-colors hover:text-white">
              {t("global", "footer_legal")}
            </Link>
            <Link to="/politique-cookies" data-testid="footer-cookies-link" className="font-poppins text-xs text-white/40 transition-colors hover:text-white">
              {t("global", "footer_cookies")}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
